import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  ArrowLeftIcon,
  BotIcon,
  CheckCircle2Icon,
  CheckIcon,
  CircleIcon,
  CopyIcon,
  GaugeIcon,
  ListIcon,
  Loader2Icon,
  MinusIcon,
  PauseIcon,
  PenLineIcon,
  PencilIcon,
  PlusIcon,
  SendIcon,
  SquareIcon,
  WrenchIcon,
} from "lucide-react";
import { toast } from "sonner";
import type { ChatMessage, Spend } from "@woodpecker/protocol";
import { EFFORTS } from "@woodpecker/protocol";
import { Button, Badge } from "../ui/button";
import { Card, Desktop, MobileItem, MobileList } from "../ui/card";
import { Table, TBody, TD, TH, THead, TR } from "../ui/table";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../ui/overlay";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Input, Label, Switch, Textarea } from "../ui/form";
import { cn } from "../lib/cn";
import { round2, short, shortTx, sum } from "../lib/format";
import { ServiceIcon } from "../lib/icons";
import { useN, useT, useTT, Y } from "../i18n";
import { useStore, type AuthRow } from "../store/store";
import { useWallet } from "../store/wallet";
import { errorText, type ConversationView } from "../adapters/clients";
import { KindBadge, SectionLabel, StatusDot, Waiting, Rejected, manageHref, useBuy } from "../components/common";
import { useLiveConversation, useMedia } from "../components/hooks";
import { capsText, useSignLines, useStopOnChain } from "../components/signing";
import { DeployCards } from "./Deploy";

const SETTLED = "text-emerald-600 dark:text-emerald-400";
const TONE = { ok: "", warn: "text-amber-600 dark:text-amber-400", crit: "text-destructive" } as const;
const RING = { ok: "stroke-primary", warn: "stroke-amber-500", crit: "stroke-destructive" } as const;
type Tone = keyof typeof TONE;
const toneOf = (remaining: number, cap: number): Tone =>
  remaining === 0 ? "crit" : remaining <= cap * 0.25 ? "warn" : "ok";

function Ring({ remaining, cap, tone, size = 20 }: { remaining: number; cap: number; tone: Tone; size?: number }) {
  const w = size > 40 ? 6 : 2.5;
  const r = (size - w) / 2;
  const c = 2 * Math.PI * r;
  const f = cap > 0 ? Math.max(0, Math.min(1, remaining / cap)) : 0;
  return (
    <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} className="shrink-0 -rotate-90" aria-hidden>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={w} className="stroke-border" />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        strokeWidth={w}
        strokeLinecap="round"
        strokeDasharray={`${f * c} ${c}`}
        className={cn("transition-[stroke-dasharray] duration-500", RING[tone])}
      />
    </svg>
  );
}

/** One message of the transcript: user, assistant, tool, a wallet card, a system note. */
export function Message({ m }: { m: ChatMessage }) {
  const t = useT();
  const tt = useTT();
  if (m.role === "user") {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-primary px-4 py-2.5 text-sm whitespace-pre-wrap text-primary-foreground">
          {m.text}
        </div>
      </div>
    );
  }
  if (m.role === "assistant") {
    return (
      <div className="flex gap-3">
        <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-muted">
          <BotIcon className="size-4" />
        </span>
        <div className="min-w-0 max-w-[85%]">
          <div className="mb-1 font-mono text-xs text-muted-foreground">
            {m.model}
            {m.spend && ` · ${m.spend}`}
          </div>
          <div className="text-sm leading-relaxed wrap-break-word whitespace-pre-wrap">{m.text}</div>
        </div>
      </div>
    );
  }
  if (m.role === "signature") {
    const sigs = m.signatures ?? [];
    const started = sigs.some((s) => s.state !== "pending");
    const all = sigs.every((s) => s.state === "signed");
    const txCount = sigs.filter((s) => !s.call).length;
    return (
      <div className="ml-10 rounded-xl border border-amber-500/40 bg-amber-500/5 p-3 text-sm">
        <div className="flex flex-wrap items-center gap-2">
          <PenLineIcon className="size-3.5 text-amber-600 dark:text-amber-400" />
          <span className="font-medium">{m.title}</span>
          <Badge variant="outline">{txCount === 1 ? t("wallet.tx1") : t("wallet.txN", { n: txCount })}</Badge>
          {all && <Badge variant="secondary">{t("common.signed")}</Badge>}
        </div>
        {m.note && <p className="mt-1 text-xs text-muted-foreground">{m.note}</p>}
        {m.rejected ? (
          <Rejected className="mt-2 text-xs" />
        ) : started ? (
          <ol className="mt-2 flex flex-col gap-1.5">
            {sigs.map((s, i) =>
              s.call && s.state === "pending" ? null : (
                <li key={i} className="flex items-start gap-2 text-xs">
                  {s.state === "signed" ? (
                    <CheckCircle2Icon className="mt-0.5 size-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
                  ) : s.state === "signing" ? (
                    <Loader2Icon className="mt-0.5 size-3.5 shrink-0 animate-spin" />
                  ) : (
                    <CircleIcon className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
                  )}
                  <span className={cn("font-mono", (s.state === "pending" || s.call) && "text-muted-foreground")}>
                    {s.text}
                    {s.state === "signed" && !s.call && (
                      <span className={SETTLED}> {t("common.signedTx", { tx: s.tx ? shortTx(s.tx) : "" })}</span>
                    )}
                    {s.state === "signing" && !s.call && (
                      <span className="text-muted-foreground"> {t("common.waitingSignature")}</span>
                    )}
                  </span>
                </li>
              ),
            )}
          </ol>
        ) : (
          <Waiting n={txCount} className="mt-2 text-xs" />
        )}
        {m.resultText && all && <div className={cn("mt-2 font-mono text-xs", SETTLED)}>→ {m.resultText}</div>}
      </div>
    );
  }
  if (m.role === "system") {
    return <div className="ml-10 text-xs text-muted-foreground">{m.text}</div>;
  }
  return (
    <div className="ml-10 rounded-xl border bg-muted/40 p-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <WrenchIcon className="size-3.5 text-muted-foreground" />
        <span className="font-mono font-medium">{m.tool}</span>
        <Badge variant="outline" className="font-mono">
          {m.price}
        </Badge>
        {m.failed ? (
          <Badge variant="destructive">{t("transcript.failed")}</Badge>
        ) : (
          <Badge variant="secondary">{tt(Y(`spendState.${m.state ?? "settled"}`))}</Badge>
        )}
      </div>
      <pre className="mt-2 font-mono text-xs wrap-break-word whitespace-pre-wrap text-muted-foreground">{m.args}</pre>
      <div className="mt-1 font-mono text-xs">→ {m.result}</div>
      {m.lines && (
        <ul className="mt-1.5 flex flex-col gap-0.5 border-l pl-3 font-mono text-xs text-muted-foreground">
          {m.lines.map((l, i) => (
            <li key={i} className="wrap-break-word">
              {l}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function Transcript({
  messages,
  running,
  empty,
  className,
}: {
  messages: ChatMessage[];
  running?: string;
  empty?: string;
  className?: string;
}) {
  const end = useRef<HTMLDivElement>(null);
  const sig = messages
    .map((m) =>
      m.role === "signature" ? (m.signatures ?? []).map((s) => s.state).join() + (m.rejected ? "!" : "") : "",
    )
    .join("|");
  useEffect(() => {
    const el = end.current;
    const parent = el?.parentElement;
    if (el && parent && (messages.length !== 0 || running)) {
      if (parent.scrollHeight > parent.clientHeight) parent.scrollTop = parent.scrollHeight;
      el.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [messages.length, running, sig]);
  return (
    <div className={cn("flex min-h-80 flex-1 flex-col gap-5 p-4", className)}>
      {messages.length === 0 && !running && empty && (
        <p className="m-auto max-w-xs text-center text-sm text-muted-foreground">{empty}</p>
      )}
      {messages.map((m, i) => (
        <Message key={i} m={m} />
      ))}
      {running && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2Icon className="size-4 shrink-0 animate-spin" />
          <span>{running}</span>
        </div>
      )}
      <div ref={end} />
    </div>
  );
}

function spendName(t: (k: string, p?: Record<string, number | string>) => string, s: Spend): string {
  if (s.kind === "runner")
    return (s.rounds ?? s.actual) === 1 ? t("spend.round1") : t("spend.roundN", { n: s.rounds ?? s.actual });
  if (s.name === "commit") return t("spend.commit");
  return s.name;
}
const spendAmount = (t: (k: string, p?: Record<string, number | string>) => string, s: Spend) =>
  s.minted
    ? t("spend.mixed", {
        amount: s.actual,
        token: s.token,
        minted: s.minted,
        credits: round2(Number(s.credits ?? 0) / 1e6),
      })
    : `${s.actual} ${s.token}`;
const serviceShort = (s: Spend) => s.service.slice(s.service.indexOf("/") + 1);

function SpendDetail({ s }: { s: Spend }) {
  const { wpPerToken, config } = useStore();
  const t = useT();
  const n = useN();
  const row = (label: string, value: ReactNode) => (
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono tabular-nums">{value}</span>
    </div>
  );
  const minted = s.minted ?? 0;
  const credits = Number(s.credits ?? 0) / 1e6;
  const rateAtSettle = minted > 0 && credits > 0 ? round2(minted / credits) : null;
  const tx = config.explorerTx ? (
    <a className="underline" href={`${config.explorerTx}${s.tx}`} target="_blank" rel="noreferrer">
      {shortTx(s.tx)}
    </a>
  ) : (
    shortTx(s.tx)
  );
  return (
    <div className="flex flex-col gap-2 text-xs">
      <div className="flex items-center gap-2">
        <KindBadge kind={s.kind} />
        <span className="font-mono text-sm">{spendName(t, s)}</span>
        <span className={cn("ml-auto", SETTLED)}>{t("spendState.settled")}</span>
      </div>
      {row(t("col.actual"), `${s.actual} ${s.token}`)}
      {row(t("col.wp"), n(round2(s.actual * wpPerToken(s.token))))}
      {rateAtSettle !== null && (
        <>
          <div className="text-muted-foreground">
            {t("conv.mixedDetail", {
              fromTokens: s.actual - minted,
              minted,
              credits: round2(credits),
              n: rateAtSettle,
              token: s.token,
            })}
          </div>
          {rateAtSettle !== round2(1 / Math.max(wpPerToken(s.token), 1e-9)) && (
            <div className="text-amber-600 dark:text-amber-400">
              {t("conv.rateNow", { n: round2(1 / Math.max(wpPerToken(s.token), 1e-9)), token: s.token })}
            </div>
          )}
        </>
      )}
      {row(t("col.tx"), tx)}
      {row(t("col.time"), s.time.slice(11, 19))}
    </div>
  );
}

function SpendsBar({ c, onAll }: { c: ConversationView; onAll: () => void }) {
  const { directory } = useStore();
  const t = useT();
  const strip = useRef<HTMLDivElement>(null);
  useEffect(() => {
    strip.current?.scrollTo({ left: 0, behavior: "smooth" });
  }, [c.spends.length]);
  return (
    <div className="relative border-t bg-muted/40">
      <div
        ref={strip}
        className="flex items-center gap-2.5 overflow-x-auto px-3 py-2.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {c.spends.length === 0 && <span className="text-xs text-muted-foreground">{t("conv.spends.empty")}</span>}
        {c.spends.map((s) => (
          <Popover key={s.id}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="relative inline-flex h-8 shrink-0 items-center gap-2 overflow-hidden rounded-full border bg-background pr-3 pl-1 text-xs transition-colors hover:bg-muted"
              >
                <span className="grid size-6 place-items-center rounded-full bg-muted">
                  <ServiceIcon entry={directory.find((e) => e.name === s.service)} kind={s.kind} className="size-3.5" />
                </span>
                <span className="font-mono">{serviceShort(s)}</span>
                <span className={cn("font-mono tabular-nums", SETTLED)}>{spendAmount(t, s)}</span>
              </button>
            </PopoverTrigger>
            <PopoverContent side="top" align="start" className="w-72">
              <SpendDetail s={s} />
            </PopoverContent>
          </Popover>
        ))}
        {c.spends.length > 0 && (
          <button
            type="button"
            onClick={onAll}
            className="ml-auto inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full border bg-background px-3 text-xs font-medium hover:bg-muted md:sticky md:right-0"
          >
            <ListIcon className="size-3.5" />
            {t("conv.spends.all", { n: c.spends.length })}
          </button>
        )}
      </div>
    </div>
  );
}

function AllSpends({
  c,
  open,
  onOpenChange,
}: {
  c: ConversationView;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const { wpPerToken } = useStore();
  const t = useT();
  const wide = useMedia("(min-width: 768px)");
  const hasBudget = c.spends.some((s) => s.credits && s.credits !== "0");
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side={wide ? "right" : "bottom"}
        className={cn("overflow-y-auto", wide ? "data-[side=right]:sm:max-w-2xl" : "max-h-[85svh]")}
      >
        <SheetHeader>
          <SheetTitle>{t("conv.spends.title")}</SheetTitle>
          <SheetDescription>{t("conv.spends.intro")}</SheetDescription>
        </SheetHeader>
        <div className="px-4 pb-4">
          <MobileList>
            {c.spends.map((s) => (
              <MobileItem key={s.id} className="text-xs">
                <div className="flex items-center gap-2">
                  <span className="font-mono">{s.time.slice(11, 19)}</span>
                  <KindBadge kind={s.kind} />
                  <span className="truncate font-mono">{spendName(t, s)}</span>
                </div>
                <span>
                  {s.minted
                    ? spendAmount(t, s)
                    : `${s.actual} ${s.token} (≈ ${round2(s.actual * wpPerToken(s.token))} wp)`}
                </span>
                <span className="font-mono text-muted-foreground">
                  {t("col.tx")} {shortTx(s.tx)}
                </span>
              </MobileItem>
            ))}
          </MobileList>
          <Desktop>
            <Table className="text-[11px] [&_td]:px-1.5 [&_th]:px-1.5">
              <THead>
                <TR>
                  <TH>{t("col.time")}</TH>
                  <TH>{t("col.kind")}</TH>
                  <TH>{t("col.name")}</TH>
                  <TH className="text-right">{t("col.actual")}</TH>
                  <TH className="text-right">{t(hasBudget ? "col.paid" : "col.wp")}</TH>
                  <TH>{t("col.tx")}</TH>
                </TR>
              </THead>
              <TBody>
                {c.spends.map((s) => (
                  <TR key={s.id}>
                    <TD className="font-mono" title={s.time}>
                      {s.time.slice(11, 16)}
                    </TD>
                    <TD>
                      <KindBadge kind={s.kind} />
                    </TD>
                    <TD className="whitespace-normal">
                      <span className="font-mono wrap-anywhere">{spendName(t, s)}</span>
                    </TD>
                    <TD className="text-right tabular-nums">
                      {s.actual} {s.token}
                    </TD>
                    <TD className="text-right text-muted-foreground tabular-nums">
                      {hasBudget
                        ? s.minted
                          ? `${round2(Number(s.credits ?? 0) / 1e6)} wpCredit`
                          : "—"
                        : round2(s.actual * wpPerToken(s.token))}
                    </TD>
                    <TD className="font-mono text-muted-foreground">{shortTx(s.tx)}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </Desktop>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function CapStatus({ status }: { status: "idle" | "waiting" | "signed" | "rejected" }) {
  const t = useT();
  if (status === "waiting")
    return (
      <span className="inline-flex items-center gap-1.5 text-muted-foreground">
        <Loader2Icon className="size-3.5 animate-spin" />
        {t("conv.auth.waiting")}
      </span>
    );
  if (status === "signed")
    return (
      <span className={cn("inline-flex items-center gap-1.5", SETTLED)}>
        <CheckIcon className="size-3.5" />
        {t("conv.auth.signed")}
      </span>
    );
  if (status === "rejected") return <span className="text-destructive">{t("conv.auth.rejected")}</span>;
  return null;
}

/** The cap popover of one token: the ring, the new cap, Save → authorize (D64). */
function CapChip({
  c,
  r,
  open,
  setOpen,
  suggest,
}: {
  c: ConversationView;
  r: AuthRow;
  open: boolean;
  setOpen: (v: boolean) => void;
  suggest?: number;
}) {
  const { directory, wpPerToken, refreshAuth, runner } = useStore();
  const { request } = useWallet();
  const lines = useSignLines();
  const t = useT();
  const [draft, setDraft] = useState<number | null>(null);
  const [status, setStatus] = useState<"idle" | "waiting" | "signed" | "rejected">("idle");
  const entry = directory.find((e) => e.token === r.token);
  const cap = draft ?? Math.max(r.cap, suggest ?? 0);
  const max = Math.max(r.cap * 2, r.used + 10, 10);
  const remaining = Math.max(0, cap - r.used);
  const waiting = status === "waiting";
  const changed = !waiting && cap !== r.cap;
  const guard = (e: Event) => waiting && e.preventDefault();
  function save() {
    if (!changed) return;
    setStatus("waiting");
    request({
      title: Y("wallet.req.capChange", { title: c.title || Y("conv.untitled"), token: r.token, from: r.cap, to: cap }),
      lines: [
        lines.authorize(
          Y("wallet.plain.authorize", { title: c.title || Y("conv.untitled"), caps: `${cap} ${r.token}` }),
          c.agent,
          { [r.token]: cap },
        ),
      ],
      onDone: async (res) => {
        setStatus(res === "signed" ? "signed" : "rejected");
        if (res === "signed") {
          await refreshAuth(c.id, c.agent);
          if (c.status === "paused" && runner) await runner.resume(c.id).catch(() => undefined);
        }
      },
    });
  }
  return (
    <Popover
      open={open}
      onOpenChange={(v) => {
        if (!v && !waiting) {
          setDraft(null);
          setStatus("idle");
        }
        setOpen(v);
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={t("conv.cap.open", { token: r.token })}
          className={cn(
            "inline-flex h-8 items-center gap-1.5 rounded-full px-2 text-xs transition-colors hover:bg-muted data-[state=open]:bg-muted",
            TONE[toneOf(r.remaining, r.cap)],
          )}
        >
          <Ring remaining={r.remaining} cap={r.cap} tone={toneOf(r.remaining, r.cap)} />
          <span className="font-mono tabular-nums">
            {r.remaining}/{r.cap} {r.token}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="start"
        sideOffset={10}
        className="w-76 p-0"
        onInteractOutside={guard}
        onFocusOutside={guard}
      >
        <div className="flex items-center gap-2.5 px-4 pt-4">
          <ServiceIcon entry={entry} kind={entry?.kinds[0]} />
          <div className="min-w-0">
            <div className="truncate font-mono text-sm">{entry?.name ?? r.token}</div>
            {entry && entry.labels.length > 0 && (
              <div className="truncate text-xs text-muted-foreground">{entry.labels[0]}</div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-4 px-4 py-4">
          <div className="relative grid size-20 shrink-0 place-items-center">
            <span className="absolute inset-0">
              <Ring remaining={remaining} cap={cap} tone={toneOf(remaining, cap)} size={80} />
            </span>
            <span className="relative text-center leading-none tabular-nums">
              <span className={cn("block text-xl font-semibold", TONE[toneOf(remaining, cap)])}>{remaining}</span>
              <span className="text-[11px] text-muted-foreground">/ {cap}</span>
            </span>
          </div>
          <div className="flex min-w-0 flex-col gap-1 text-xs text-muted-foreground">
            <span className="text-sm text-foreground">
              {t("conv.cap.remaining", { remaining, cap, token: r.token })}
            </span>
            {cap < r.used ? (
              <span className="text-amber-600 dark:text-amber-400">{t("conv.auth.belowUsed", { n: r.used })}</span>
            ) : (
              <span>{t("conv.cap.used", { n: r.used })}</span>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-3 px-4 pb-4">
          <SectionLabel>{t("conv.auth.newCap")}</SectionLabel>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon-sm"
              aria-label={t("conv.cap.lower")}
              disabled={waiting || cap <= 0}
              onClick={() => setDraft(Math.max(0, cap - 1))}
            >
              <MinusIcon />
            </Button>
            <Input
              type="number"
              min={0}
              step={1}
              aria-label={`${t("conv.auth.newCap")} ${r.token}`}
              className="h-8 w-20 text-center tabular-nums"
              value={cap}
              disabled={waiting}
              onChange={(e) => setDraft(Math.max(0, Math.floor(Number(e.target.value) || 0)))}
            />
            <Button
              variant="outline"
              size="icon-sm"
              aria-label={t("conv.cap.raise")}
              disabled={waiting}
              onClick={() => setDraft(cap + 1)}
            >
              <PlusIcon />
            </Button>
            <span className="font-mono text-xs text-muted-foreground">{r.token}</span>
          </div>
          <input
            type="range"
            min={0}
            max={Math.max(max, cap)}
            step={1}
            value={cap}
            disabled={waiting}
            aria-label={`${t("conv.auth.newCap")} ${r.token}`}
            onChange={(e) => setDraft(Number(e.target.value))}
            className="w-full accent-primary"
          />
          <span className="text-xs text-muted-foreground tabular-nums">
            {t("conv.cap.newWp", { wp: round2(cap * wpPerToken(r.token)), cap, token: r.token })}
          </span>
        </div>
        <div className="flex items-center justify-between gap-3 border-t bg-muted/40 px-4 py-3 text-xs">
          <CapStatus status={status} />
          <Button size="sm" className="ml-auto" onClick={save} disabled={!changed}>
            {waiting && <Loader2Icon className="animate-spin" />}
            {t("conv.cap.save")}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** Pay in wpCredit on a conversation that exists: a budget for the agent, or cap = used to switch it off (D67). */
function BudgetSwitch({ c, rows }: { c: ConversationView; rows: AuthRow[] }) {
  const { approvedTokens, refreshAuth, wpPerToken, runner } = useStore();
  const { request } = useWallet();
  const lines = useSignLines();
  const t = useT();
  const on = rows.some((r) => r.token === "wpCredit" && r.remaining > 0);
  const used = rows.find((r) => r.token === "wpCredit")?.used ?? 0;
  const prefill = sum(
    used,
    ...rows.filter((r) => r.token !== "wpCredit").map((r) => r.remaining * wpPerToken(r.token)),
  );
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<string | null>(null);
  const [status, setStatus] = useState<"idle" | "waiting" | "signed" | "rejected">("idle");
  const value = draft === null ? prefill : round2(Number(draft) || 0);
  const waiting = status === "waiting";
  function submit() {
    if (value <= used) return;
    setStatus("waiting");
    const approve = !approvedTokens.has("wpCredit");
    request({
      title: Y("wallet.req.budget", { title: c.title || Y("conv.untitled"), cap: value }),
      amount: value,
      tokens: [{ amount: value, symbol: "wpCredit" }],
      lines: [
        ...(approve ? [lines.approve("wpCredit")] : []),
        lines.authorize(Y("wallet.plain.budget", { cap: value }), c.agent, { wpCredit: value }),
      ],
      onDone: async (r) => {
        setStatus(r === "signed" ? "signed" : "rejected");
        if (r === "signed") {
          await refreshAuth(c.id, c.agent);
          setOpen(false);
          toast.success(t("budget.signed", { cap: value }));
          if (c.status === "paused" && runner) await runner.resume(c.id).catch(() => undefined);
        }
      },
    });
  }
  function turnOff() {
    setStatus("waiting");
    request({
      title: Y("wallet.req.budgetOff", { title: c.title || Y("conv.untitled") }),
      lines: [lines.authorize(Y("wallet.plain.budgetOff", { used }), c.agent, { wpCredit: used })],
      onDone: async (r) => {
        setStatus(r === "signed" ? "idle" : "rejected");
        if (r === "signed") {
          await refreshAuth(c.id, c.agent);
          toast(t("budget.turnedOff"));
        }
      },
    });
  }
  return (
    <Popover open={open} onOpenChange={(v) => !waiting && setOpen(v)}>
      <PopoverAnchor asChild>
        <label className="inline-flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground sm:ml-auto">
          {t("dialog.payInWp")}
          <Switch
            checked={on}
            disabled={waiting || c.kind === "deploy"}
            aria-label={t("dialog.payInWp")}
            onCheckedChange={(v) => {
              if (!v) return turnOff();
              setDraft(null);
              setStatus("idle");
              setOpen(true);
            }}
          />
        </label>
      </PopoverAnchor>
      <PopoverContent side="top" align="end" className="w-80" onInteractOutside={(e) => waiting && e.preventDefault()}>
        <div className="flex flex-col gap-2 text-sm">
          <div className="font-medium">{t("dialog.payInWp")}</div>
          <div className="flex flex-wrap items-end gap-x-3 gap-y-1">
            <div className="grid gap-1">
              <Label htmlFor="conv-budget" className="text-xs font-normal text-muted-foreground">
                {t("budget.cap")}
              </Label>
              <Input
                id="conv-budget"
                type="number"
                min={round2(used + 0.01)}
                step="0.01"
                className="w-28 tabular-nums"
                value={draft ?? prefill}
                disabled={waiting}
                onChange={(e) => setDraft(e.target.value)}
              />
            </div>
            {draft === null && (
              <span className="pb-1.5 text-[11px] text-muted-foreground">{t("budget.prefillRemaining")}</span>
            )}
          </div>
          <p className="text-xs text-muted-foreground">{t("budget.onBar")}</p>
          <div className="flex items-center justify-between gap-2">
            <CapStatus status={status === "signed" ? "idle" : status} />
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setOpen(false)} disabled={waiting}>
                {t("common.cancel")}
              </Button>
              <Button size="sm" onClick={submit} disabled={value <= used || waiting}>
                {waiting && <Loader2Icon className="animate-spin" />}
                {t("budget.submit")}
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function StopButton({ c, className }: { c: ConversationView; className?: string }) {
  const { runner, setConversation } = useStore();
  const t = useT();
  if (c.kind === "deploy" || c.status === "stopped" || c.status === "done") return null;
  return (
    <Button
      variant="outline"
      size="sm"
      className={cn("text-destructive hover:text-destructive", className)}
      onClick={async () => {
        if (!runner) return;
        const v = await runner.stop(c.id);
        setConversation(v);
        toast(t("conv.stop.toast"));
      }}
    >
      <SquareIcon />
      {t("common.stop")}
    </Button>
  );
}

/** The authorization table of the "≈ wp remaining" popover: every cap editable, one multicall for the changes. */
function AuthTable({
  c,
  rows,
  status,
  setStatus,
}: {
  c: ConversationView;
  rows: AuthRow[];
  status: "idle" | "waiting" | "signed" | "rejected";
  setStatus: (s: "idle" | "waiting" | "signed" | "rejected") => void;
}) {
  const { wpPerToken, refreshAuth, runner } = useStore();
  const { request } = useWallet();
  const lines = useSignLines();
  const t = useT();
  const n = useN();
  const [drafts, setDrafts] = useState<Record<string, number>>({});
  const capOf = (r: AuthRow) => drafts[r.token] ?? r.cap;
  const changed = rows.filter((r) => capOf(r) !== r.cap);
  const below = changed.filter((r) => capOf(r) < r.used);
  const waiting = status === "waiting";
  function submit() {
    if (changed.length === 0) return;
    const to = Object.fromEntries(changed.map((r) => [r.token, capOf(r)]));
    setStatus("waiting");
    request({
      title:
        changed.length === 1
          ? Y("wallet.req.capChange", {
              title: c.title || Y("conv.untitled"),
              token: changed[0]!.token,
              from: changed[0]!.cap,
              to: capOf(changed[0]!),
            })
          : Y("wallet.req.capChanges", {
              title: c.title || Y("conv.untitled"),
              changes: changed.map((r) => `${r.token} ${r.cap} → ${capOf(r)}`).join(" · "),
            }),
      lines: [
        lines.authorize(
          Y("wallet.plain.authorize", { title: c.title || Y("conv.untitled"), caps: capsText(to) }),
          c.agent,
          to,
        ),
      ],
      onDone: async (res) => {
        setStatus(res === "signed" ? "signed" : "rejected");
        if (res === "signed") {
          setDrafts({});
          await refreshAuth(c.id, c.agent);
          if (c.status === "paused" && runner) await runner.resume(c.id).catch(() => undefined);
        }
      },
    });
  }
  const grid =
    "grid items-center gap-x-3 grid-cols-[4.5rem_repeat(2,minmax(0,1fr))] sm:grid-cols-[minmax(0,1.6fr)_5.5rem_repeat(3,minmax(0,1fr))]";
  const first = "min-w-0 max-sm:col-span-full";
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(c.agent);
      toast.success(t("conv.identity.copied"));
    } catch {
      toast.error(t("wallet.copyBlocked"));
    }
  };
  return (
    <div className="flex flex-col text-sm">
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-1 px-5 py-4 max-sm:px-4">
        <div className="min-w-0">
          <SectionLabel>{t("conv.identity.label")}</SectionLabel>
          <div className="mt-0.5 flex items-center gap-1">
            <code className="font-mono">{short(c.agent)}</code>
            <Button variant="ghost" size="icon-xs" aria-label={t("conv.identity.copy")} onClick={copy}>
              <CopyIcon />
            </Button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground sm:max-w-72 sm:text-right">{t("conv.identity.note")}</p>
      </div>
      <div className="border-t px-5 py-4 max-sm:px-4">
        <SectionLabel>{t("conv.auth.title")}</SectionLabel>
        <div className={cn(grid, "mt-3 border-b pb-2 text-[11px] font-medium whitespace-nowrap text-muted-foreground")}>
          <span className={cn(first, "max-sm:mb-1")}>{t("col.token")}</span>
          <span className="text-right">{t("col.cap")}</span>
          <span className="text-right">{t("col.used")}</span>
          <span className="text-right">{t("col.remaining")}</span>
          <span className="text-right max-sm:hidden">{t("col.wp")}</span>
        </div>
        <ul className="divide-y">
          {rows.map((r) => {
            const low = capOf(r) < r.used;
            return (
              <li key={r.token} className={cn(grid, "py-2.5 text-xs")}>
                <span className={cn(first, "max-sm:mb-1.5")}>
                  <span className="flex items-center gap-1.5">
                    <Ring remaining={r.remaining} cap={r.cap} tone={toneOf(r.remaining, r.cap)} />
                    <span className="truncate font-mono">{r.token}</span>
                  </span>
                </span>
                <span className="flex justify-end">
                  <Input
                    type="number"
                    min={0}
                    step={r.token === "wpCredit" ? "0.01" : 1}
                    aria-label={`${t("conv.auth.newCap")} ${r.token}`}
                    title={low ? t("conv.auth.belowUsed", { n: r.used }) : undefined}
                    className={cn(
                      "h-7 w-full max-w-20 px-1.5 text-right text-xs tabular-nums",
                      low && "text-amber-600 dark:text-amber-400",
                    )}
                    value={capOf(r)}
                    disabled={waiting}
                    onChange={(e) =>
                      setDrafts((d) => ({
                        ...d,
                        [r.token]: Math.max(
                          0,
                          r.token === "wpCredit"
                            ? round2(Number(e.target.value) || 0)
                            : Math.floor(Number(e.target.value) || 0),
                        ),
                      }))
                    }
                  />
                </span>
                <span className="text-right tabular-nums">{n(r.used)}</span>
                <span className={cn("text-right font-medium tabular-nums", TONE[toneOf(r.remaining, r.cap)])}>
                  {n(r.remaining)}
                </span>
                <span className="text-right text-muted-foreground tabular-nums max-sm:hidden">
                  {n(round2(r.remaining * wpPerToken(r.token)))}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 border-t bg-muted/40 px-5 py-3 text-xs max-sm:px-4">
        <span className="flex min-w-0 flex-col gap-0.5">
          <CapStatus status={status} />
          {status === "idle" &&
            below.map((r) => (
              <span key={r.token} className="text-amber-600 dark:text-amber-400">
                <span className="font-mono">{r.token}</span> · {t("conv.auth.belowUsed", { n: r.used })}
              </span>
            ))}
        </span>
        <span className="ml-auto flex flex-wrap items-center gap-2">
          <StopButton c={c} />
          <Button size="sm" onClick={submit} disabled={waiting || changed.length === 0}>
            {waiting && <Loader2Icon className="animate-spin" />}
            {t("conv.auth.submit")}
          </Button>
        </span>
      </div>
    </div>
  );
}

function PauseBar({ c, rows, onRaise }: { c: ConversationView; rows: AuthRow[]; onRaise: (token: string) => void }) {
  const { balances, runner, directory } = useStore();
  const { onBuy } = useBuy();
  const t = useT();
  const p = c.status === "paused" ? c.pause : undefined;
  if (!p) return null;
  const balance = balances[p.token] ?? 0;
  const isCap = p.reason === "cap" || p.reason === "budget";
  // the service's price document changed since its owner committed its hash (item 11): the owner commits the new
  // hash on the management page (Verify again, or Commit price hash); then Resend settles the held call
  const pricing = p.reason === "pricing";
  const ownService = pricing && !!p.service && directory.find((e) => e.name === p.service)?.mine;
  const unexpected = p.reason === "unknown" || p.reason === "rejected" || p.reason === "nonce";
  const hasBudget = rows.some((r) => r.token === "wpCredit" && r.remaining > 0);
  return (
    <div
      role="status"
      className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-t bg-amber-500/10 px-3 py-2 text-xs"
    >
      <PauseIcon className="size-4 shrink-0 text-amber-600 dark:text-amber-400" />
      <span className="min-w-0 flex-1">
        {pricing
          ? t("conv.pause.pricing", { service: p.service ?? p.token })
          : unexpected
            ? t("conv.pause.unexpected", { service: p.service ?? p.token })
            : isCap
              ? t("conv.pause.cap", { token: p.token })
              : t("conv.pause.balance", { balance, token: p.token })}
        {!pricing && !unexpected && !isCap && !hasBudget && ` ${t("conv.pause.budgetHint")}`}
        {ownService && (
          <>
            {" "}
            <a className="font-medium underline" href={manageHref(p.service!)}>
              {t("conv.pause.pricingManage")}
            </a>
          </>
        )}
        {p.detail && <span className="text-muted-foreground"> · {p.detail}</span>}
      </span>
      {!pricing && !unexpected && (
        <Button size="xs" variant="outline" onClick={() => (isCap ? onRaise(p.token) : onBuy(p.token))}>
          {t(isCap ? "conv.pause.raise" : "conv.pause.buy")}
        </Button>
      )}
      <Button
        size="xs"
        variant="outline"
        title={t("conv.resendHint")}
        onClick={() => runner?.resume(c.id).catch((e: Error) => toast.error(e.message))}
      >
        {t("conv.resend")}
      </Button>
    </div>
  );
}

function StopBar({ c, rows }: { c: ConversationView; rows: AuthRow[] }) {
  const t = useT();
  const stopOnChain = useStopOnChain();
  const [status, setStatus] = useState<"idle" | "waiting" | "rejected">("idle");
  if (c.status !== "stopped") return null;
  const open = rows.filter((r) => r.remaining > 0);
  const closed = open.length === 0;
  const waiting = status === "waiting";
  return (
    <div
      role="status"
      className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-t bg-destructive/10 px-3 py-2 text-xs"
    >
      <SquareIcon className="size-4 shrink-0 text-destructive" />
      <span className="min-w-0 flex-1">{t(closed ? "conv.stop.chainLine" : "conv.stop.line")}</span>
      {status === "rejected" && <Rejected className="text-xs" />}
      {!closed && (
        <Button
          size="xs"
          variant="outline"
          disabled={waiting}
          onClick={() => {
            setStatus("waiting");
            stopOnChain(c, open, (r) => setStatus(r === "signed" ? "idle" : "rejected"));
          }}
        >
          {waiting && <Loader2Icon className="animate-spin" />}
          {t(waiting ? "conv.auth.stopping" : "conv.stop.onChain")}
        </Button>
      )}
    </div>
  );
}

function Composer({ c }: { c: ConversationView }) {
  const { runner, setConversation } = useStore();
  const t = useT();
  const [text, setText] = useState("");
  const running = c.status === "running";
  const paused = c.status === "paused";
  const stopped = c.status === "stopped";
  async function send() {
    const v = text.trim();
    if (!v || !runner) return;
    try {
      await runner.message(c.id, v);
      setText("");
      setConversation({ ...c, status: "running", messages: [...c.messages, { role: "user", text: v }] });
    } catch (e) {
      toast.error(t("conv.sendFailed", { detail: errorText(e) }));
    }
  }
  if (c.kind === "deploy") {
    return (
      <div className="flex flex-wrap items-center justify-between gap-3 border-t p-3 text-sm text-muted-foreground">
        <span>{t(c.status === "done" ? "conv.deployDone" : "conv.deployRuns")}</span>
        {c.status !== "done" && (
          <Button variant="outline" size="sm" asChild>
            <a href="#/services/deploy">{t("conv.continueOnDeploy")}</a>
          </Button>
        )}
      </div>
    );
  }
  return (
    <div className="flex items-end gap-2 border-t p-3">
      <Textarea
        rows={2}
        className="min-h-0 resize-none"
        placeholder={t(
          running
            ? "conv.placeholder.wait"
            : paused
              ? "conv.placeholder.paused"
              : stopped
                ? "conv.placeholder.stopped"
                : "conv.placeholder.write",
        )}
        value={text}
        disabled={running || paused || stopped}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            void send();
          }
        }}
      />
      <Button
        size="icon-lg"
        onClick={() => void send()}
        disabled={running || paused || stopped || !text.trim()}
        aria-label={t("common.send")}
      >
        <SendIcon />
      </Button>
    </div>
  );
}

/** The bar under the transcript: the spends, the meters, Pay in wpCredit, "≈ wp remaining", the pause and stop bars, the composer. */
function AuthBar({ c, onAll }: { c: ConversationView; onAll: () => void }) {
  const { auths, wpPerToken } = useStore();
  const t = useT();
  const wide = useMedia("(min-width: 768px)");
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<"idle" | "waiting" | "signed" | "rejected">("idle");
  const [chip, setChip] = useState<string | null>(null);
  const rows = auths[c.id] ?? [];
  const shown = rows.filter((r) => r.token !== "wpCredit" || r.remaining > 0);
  const remainingWp = sum(...shown.filter((r) => r.token !== "wpCredit").map((r) => r.remaining * wpPerToken(r.token)));
  const pause = c.status === "paused" ? c.pause : undefined;
  const needs = pause?.needs ? Number(pause.needs) : 0;
  const guard = (e: Event) => status === "waiting" && e.preventDefault();
  const table = <AuthTable c={c} rows={shown} status={status} setStatus={setStatus} />;
  const trigger = (
    <button
      type="button"
      aria-label={t("conv.auth.button")}
      aria-expanded={open}
      onClick={wide ? undefined : () => setOpen(true)}
      className="ml-auto inline-flex h-8 shrink-0 items-center gap-2 rounded-full px-2.5 text-sm font-semibold whitespace-nowrap tabular-nums transition-colors hover:bg-muted data-[state=open]:bg-muted"
    >
      {t("conv.auth.remainingWp", { wp: remainingWp })}
      <GaugeIcon className="size-4 text-muted-foreground" />
    </button>
  );
  const body = (
    <div>
      <SpendsBar c={c} onAll={onAll} />
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-t px-2 py-1.5">
        <div className="flex min-w-0 flex-wrap items-center gap-x-1 gap-y-0.5 max-sm:w-full">
          {shown.map((r) => (
            <CapChip
              key={r.token}
              c={c}
              r={r}
              open={chip === r.token}
              setOpen={(v) => setChip(v ? r.token : null)}
              suggest={
                pause && (pause.reason === "cap" || pause.reason === "budget") && pause.token === r.token
                  ? r.used + needs
                  : undefined
              }
            />
          ))}
        </div>
        <BudgetSwitch c={c} rows={rows} />
        {wide ? <PopoverTrigger asChild>{trigger}</PopoverTrigger> : trigger}
      </div>
      <PauseBar c={c} rows={rows} onRaise={(tok) => setChip(tok)} />
      <StopBar c={c} rows={rows} />
      <DeployCards c={c} />
      <Composer c={c} />
    </div>
  );
  return wide ? (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>{body}</PopoverAnchor>
      <PopoverContent
        side="top"
        align="end"
        sideOffset={8}
        className="w-(--radix-popover-trigger-width) max-w-[calc(100vw-2rem)] p-0"
        onInteractOutside={guard}
        onFocusOutside={guard}
      >
        {table}
      </PopoverContent>
    </Popover>
  ) : (
    <>
      {body}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="bottom"
          className="max-h-[85svh] overflow-y-auto p-0"
          onInteractOutside={guard}
          onFocusOutside={guard}
        >
          <SheetHeader className="sr-only">
            <SheetTitle>{t("conv.auth.button")}</SheetTitle>
            <SheetDescription>{t("conv.auth.title")}</SheetDescription>
          </SheetHeader>
          {table}
        </SheetContent>
      </Sheet>
    </>
  );
}

function Title({ c }: { c: ConversationView }) {
  const { runner, setConversation } = useStore();
  const t = useT();
  const [draft, setDraft] = useState<string | null>(null);
  async function done() {
    if (draft !== null && runner && draft.trim() && draft.trim() !== c.title)
      setConversation(await runner.patch(c.id, { title: draft.trim() }));
    setDraft(null);
  }
  return draft === null ? (
    <h1 className="flex items-center gap-1 text-2xl font-semibold tracking-tight">
      <span className="min-w-0 wrap-break-word">{c.title || t("conv.untitled")}</span>
      <Button
        variant="ghost"
        size="icon-sm"
        className="text-muted-foreground"
        aria-label={t("conv.rename")}
        title={t("conv.rename")}
        onClick={() => setDraft(c.title)}
      >
        <PencilIcon />
      </Button>
      {c.kind === "deploy" && (
        <Badge variant="outline" className="ml-2 h-4 px-1.5 text-[10px] font-normal">
          {t("common.deployBadge")}
        </Badge>
      )}
    </h1>
  ) : (
    <Input
      autoFocus
      onFocus={(e) => e.target.select()}
      aria-label={t("conv.titleLabel")}
      className="h-9 w-full max-w-xl text-lg font-semibold md:text-lg"
      value={draft}
      maxLength={80}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => void done()}
      onKeyDown={(e) => (e.key === "Enter" ? void done() : e.key === "Escape" ? setDraft(null) : undefined)}
    />
  );
}

function Chips({ c }: { c: ConversationView }) {
  const { directory, runner, setConversation } = useStore();
  const t = useT();
  const [gw, model] = c.model.split("|");
  const chip = (
    key: string,
    entry: ReturnType<typeof directory.find>,
    kind: "runner" | "memory" | "model" | "tool",
    label: string,
  ) => (
    <span key={key} className="inline-flex min-w-0 items-center gap-1.5" title={t(`kind.${kind}`)}>
      <ServiceIcon entry={entry} kind={kind} />
      <span className="truncate font-mono text-xs">{label}</span>
    </span>
  );
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-muted-foreground">
      {chip(
        "runner",
        directory.find((e) => e.name === c.runner),
        "runner",
        c.runner,
      )}
      {chip(
        "memory",
        directory.find((e) => e.name === c.memory),
        "memory",
        c.memory,
      )}
      {chip(
        "model",
        directory.find((e) => e.name === gw),
        "model",
        `${gw} · ${model}`,
      )}
      {c.tools.map((tool) =>
        chip(
          tool,
          directory.find((e) => e.tool === tool),
          "tool",
          tool,
        ),
      )}
      <Select
        value={c.effort}
        onValueChange={async (v) => {
          if (!runner) return;
          setConversation(await runner.patch(c.id, { effort: v }));
          toast(t("effort.changed", { level: v }));
        }}
      >
        <SelectTrigger
          size="sm"
          aria-label={t("effort.change")}
          className="h-6 gap-1 rounded-full px-2 font-mono text-xs text-muted-foreground shadow-none data-[size=sm]:h-6 [&>svg]:size-3"
        >
          <span className="inline-flex items-center gap-1.5">
            <GaugeIcon className="size-4.5 text-muted-foreground" strokeWidth={1.75} />
            {t("effort.chip", { level: c.effort })}
          </span>
        </SelectTrigger>
        <SelectContent position="popper" align="start">
          {EFFORTS.map((e) => (
            <SelectItem key={e} value={e} className="font-mono">
              {e}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export function ConversationPage({ id }: { id: string }) {
  const { signedIn, conversations } = useStore();
  const t = useT();
  const live = useLiveConversation(signedIn ? id : null);
  const c = live ?? conversations.find((x) => x.id === id);
  const [all, setAll] = useState(false);
  if (!c) {
    return (
      <div className="py-16 text-center">
        <p className="text-muted-foreground">{signedIn ? t("conv.notFound") : t("signedOut.conversations")}</p>
        <Button variant="link" asChild>
          <a href="#/conversations">{t("conv.backLink")}</a>
        </Button>
      </div>
    );
  }
  const running = c.status === "running";
  return (
    <>
      <div className="mb-4 flex flex-col gap-2">
        <a
          href="#/conversations"
          className="inline-flex w-fit items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeftIcon className="size-4" />
          {t("conv.back")}
        </a>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <Title c={c} />
          <StatusDot status={c.status} />
          <StopButton c={c} className="ml-auto" />
        </div>
        <Chips c={c} />
      </div>
      <Card className="min-w-0 gap-0 py-0">
        <Transcript
          messages={c.messages}
          className="md:max-h-[calc(100svh-21rem)] md:overflow-y-auto"
          running={running && c.kind !== "deploy" ? t("conv.runningPlain") : undefined}
          empty={t(c.kind === "deploy" ? "conv.empty.deploy" : "conv.empty.new")}
        />
        <AuthBar c={c} onAll={() => setAll(true)} />
      </Card>
      <AllSpends c={c} open={all} onOpenChange={setAll} />
    </>
  );
}
